import useDateFormatters from "@hooks/useDateFormatters"

import { faIcon } from "@utils/faIcon"

import { Avatar } from "@atoms"

import {
    CreditNoteTypeBadge,
    IssueDateBadge,
    LabelsBadges,
    LastModifiedBadge,
    TotalBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { CreditNoteCardProps } from "./CreditNoteCard.types"

const clientIcon = faIcon({ name: "building", style: "solid" })

type CreditNoteTimestampKey = "created_at" | "last_sent_at" | "last_voided_at"

const STATUS_TRANSITION: Record<CreditNote["status_label"], { verb: string; timestampKey: CreditNoteTimestampKey }> = {
    Draft: { verb: "Created", timestampKey: "created_at" },
    Unsent: { verb: "Created", timestampKey: "created_at" },
    Issued: { verb: "Issued", timestampKey: "last_sent_at" },
    Voided: { verb: "Voided", timestampKey: "last_voided_at" },
}

export default function CreditNoteCard(props: CreditNoteCardProps) {
    const { creditNote, displayedAttributes, onClick, isLoading = false, isTabbable, className, ...errorProps } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    const { verb, timestampKey } = STATUS_TRANSITION[creditNote.status_label]
    const statusTooltip = formatDatetimeTooltip(verb, creditNote[timestampKey] ?? creditNote.created_at)

    const showType =
        shouldShowAttribute(displayedAttributes, "credit_note_type_label") &&
        Boolean(creditNote.credit_note_type_label)
    const showTotal = shouldShowAttribute(displayedAttributes, "total") && creditNote.total != null
    const showIssued = shouldShowAttribute(displayedAttributes, "date_issued") && Boolean(creditNote.date_issued)
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(creditNote.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && creditNote.labels.length > 0

    const hasAttributes = showType || showTotal || showIssued || showLastModified || showLabels

    return (
        <ObjectCard
            onClick={onClick}
            isLoading={isLoading}
            isTabbable={isTabbable}
            className={className}
            {...errorProps}
        >
            <ObjectCard.Header
                id={creditNote.custom_id ?? String(creditNote.id)}
                subId={creditNote.invoice_id || undefined}
                subIdLabel="Invoice ID"
                statusBadge={
                    <ObjectStatusBadge
                        objectType="credit_note"
                        status={creditNote.status_label}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body
                avatar={<Avatar variant="object" icon={clientIcon} size="xl" />}
                primaryText={creditNote.client_name}
                secondaryText={creditNote.billing_address_formatted ?? undefined}
                secondaryTextPlaceholder="No billing address"
                secondaryTextTooltip={creditNote.billing_address_formatted_multiline ?? undefined}
            />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showType && <CreditNoteTypeBadge type={creditNote.credit_note_type_label} />}
                    {showTotal && <TotalBadge value={creditNote.total} />}
                    {showIssued && <IssueDateBadge date={creditNote.date_issued} />}
                    {showLastModified && <LastModifiedBadge date={creditNote.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={creditNote.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
